import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconDownload, IconSearch } from "@tabler/icons-react";
import {
  COMPLIANCE_ACTIVITIES,
  COMPLIANCE_ACTIVITY_VALUES,
} from "@congrs/schemas";
import dayjs from "dayjs";

import { DataTable } from "../DataTable";
import {
  useGetComplianceActivity,
  useExportComplianceActivity,
} from "../../api/compliance";

const PAGE_SIZE = 25;

const ACTIVITY_LABELS = {
  [COMPLIANCE_ACTIVITIES.ACCEPTED]: "Accepted",
  [COMPLIANCE_ACTIVITIES.DECLINED]: "Declined",
  // Spelled out rather than a bare "Opted out", because it sits one row away
  // from "Declined" and the two are different evidence: a decliner never
  // agreed, while this person did, and the acceptance stood until they took it
  // back. An organizer scanning the column has to read that difference without
  // being told it.
  [COMPLIANCE_ACTIVITIES.OPTED_OUT]: "Withdrew acceptance",
  [COMPLIANCE_ACTIVITIES.ACCOUNT_DELETED]: "Account deleted",
};

const ACTIVITY_COLORS = {
  [COMPLIANCE_ACTIVITIES.ACCEPTED]: "green",
  [COMPLIANCE_ACTIVITIES.DECLINED]: "red",
  [COMPLIANCE_ACTIVITIES.OPTED_OUT]: "orange",
  [COMPLIANCE_ACTIVITIES.ACCOUNT_DELETED]: "grape",
};

const FILTER_OPTIONS = COMPLIANCE_ACTIVITY_VALUES.map((value) => ({
  value,
  label: ACTIVITY_LABELS[value] ?? value,
}));

/**
 * The one line of context a row needs beyond its label. Kept out of the
 * activity column so the column stays scannable, and only written for the one
 * activity that genuinely leaves a question open: an answer is always the
 * attendee's own, but a deletion could be theirs or an admin's.
 */
const describeDetails = (row) => {
  if (row.activity === COMPLIANCE_ACTIVITIES.ACCOUNT_DELETED) {
    return row.isSelfService ? "By the account holder" : "By an admin";
  }

  return null;
};

/**
 * What the event's attendees did with its terms, newest first — every answer,
 * and every account that went.
 *
 * It is append-only, which is the point: an acceptance that was later withdrawn
 * leaves both rows standing, and an organizer's edit clears the live responses
 * without touching the answers recorded here. Nothing the organizer does to the
 * gate is a row — this table is evidence about people. The current state of who
 * is covered right now is the Insights section's question, not this table's.
 */
export default function ActivityLogTable({ eventId }) {
  const [activity, setActivity] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // The list is paginated server-side, so every keystroke would otherwise be a
  // round trip and a page reset.
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data, isPending } = useGetComplianceActivity({
    eventId,
    activity,
    search: debouncedSearch,
    page,
    limit: PAGE_SIZE,
  });

  const { mutate: exportActivity, isPending: isExporting } =
    useExportComplianceActivity();

  const columns = useMemo(
    () => [
      {
        accessor: "occurredAt",
        title: "Timestamp",
        width: 180,
        render: (row) => (
          <Text size="sm" ff="monospace">
            {dayjs(row.occurredAt).format("YYYY-MM-DD HH:mm:ss")}
          </Text>
        ),
      },
      {
        accessor: "activity",
        title: "Activity",
        width: 210,
        render: (row) => (
          <Stack gap={2}>
            <Badge
              variant="light"
              color={ACTIVITY_COLORS[row.activity] ?? "gray"}
              style={{ textTransform: "none" }}
            >
              {ACTIVITY_LABELS[row.activity] ?? row.activity}
            </Badge>
            {describeDetails(row) && (
              <Text size="xs" c="dimmed">
                {describeDetails(row)}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        accessor: "email",
        title: "Email",
        render: (row) => <Text size="sm">{row.email || "—"}</Text>,
      },
    ],
    []
  );

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={4}>Activity log</Title>
            <Text size="sm" c="dimmed">
              What attendees did with this event&apos;s compliance terms, newest
              first. Nothing here is ever overwritten — the timestamp is what
              makes the export evidence rather than a mailing list.
            </Text>
          </div>
          <Group>
            <TextInput
              placeholder="Search name or email"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setPage(1);
              }}
              w={220}
            />
            <Select
              placeholder="All activities"
              data={FILTER_OPTIONS}
              value={activity}
              onChange={(value) => {
                setActivity(value);
                setPage(1);
              }}
              clearable
              w={220}
            />
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              loading={isExporting}
              onClick={() =>
                exportActivity({ eventId, activity, search: debouncedSearch })
              }
            >
              Export
            </Button>
          </Group>
        </Group>

        <DataTable
          columns={columns}
          records={data?.data ?? []}
          fetching={isPending}
          totalRecords={data?.pagination?.total ?? data?.data?.length ?? 0}
          recordsPerPage={PAGE_SIZE}
          page={page}
          onPageChange={setPage}
          noRecordsText="Nothing has happened yet"
        />
      </Stack>
    </Card>
  );
}
